import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { useSpecialties } from 'data/layout/actions'
import { fetchDispatch } from 'data/helpers'
import { showSnackbar, snackbarTypes } from 'src/redux/snackbar'
import GenericCollapsableSection from 'components/Drawers/GenericComponents/GenericCollapsableSection'
import BasicsSection from 'pages/Hospitals/Jobs/sections/BasicsSection'
import LocationSection from 'pages/Hospitals/Jobs/sections/LocationSection'
import CompensationSection from 'pages/Hospitals/Jobs/sections/CompensationSection'
import VisaSection from 'pages/Hospitals/Jobs/sections/VisaSection'
import DescriptionSection from 'pages/Hospitals/Jobs/sections/DescriptionSection'

const NURSE_CANDIDATE_TYPE_ID = 2

const KioskJobFormStep = ({ wizardData, setWizardData, onNext, onBack }) => {
  const dispatch = useDispatch()
  const { specialties, subspecialties, candidateTypes } = useSpecialties()

  const [values, setValues] = useState({
    about: null,
    aboutRaw: null,
    title: null,
    candidateTypeId: null,
    city: null,
    state: null,
    practiceType: null,
    salaryType: null,
    specialtyIds: [],
    subspecialtyIds: [],
    visaIds: [],
    loanForgiveness: null,
    pslfCompatible: null,
    area: null,
    academicType: null,
    shiftType: null,
    salaryMin: null,
    salaryMax: null,
    salary: null,
  })

  const [formSpecialties, setFormSpecialties] = useState([])
  const [formSubspecialties, setFormSubspecialties] = useState([])
  const [showError, setShowError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const candidateTypeOptions = candidateTypes.map(({ id: typeId, name }) => ({ id: typeId, name }))

  useEffect(() => {
    if (values.candidateTypeId && specialties.length && candidateTypes.length) {
      const relevantSpecialties = specialties.filter(s =>
        candidateTypes
          .find(ct => ct.id === values.candidateTypeId)
          ?.specialtyIds.includes(s.id),
      )
      setFormSpecialties(relevantSpecialties)
    }
  }, [values.candidateTypeId, specialties, candidateTypes])

  useEffect(() => {
    if (values.specialtyIds.length && subspecialties.length && formSpecialties.length) {
      const updatedSubspecialties = subspecialties.filter(sub =>
        values.specialtyIds.some(id =>
          formSpecialties.some(sp => sp.id === id && sp.subspecialtyIds.includes(sub.id)),
        ),
      )
      setFormSubspecialties(updatedSubspecialties)
    }
  }, [values.specialtyIds, subspecialties, formSpecialties])

  const handleFieldChange = useCallback((name, val) => {
    setValues(prev => ({ ...prev, [name]: val }))
  }, [])

  const handleEditorChange = useCallback(({ about, aboutRaw }) => {
    setValues(prev => ({ ...prev, about, aboutRaw }))
  }, [])

  const missingRequiredFields = useMemo(() => {
    const fields = []
    if (!values.title) fields.push('Job Title')
    if (!values.candidateTypeId) fields.push('Profession')
    if (!values.practiceType) fields.push('Practice Type')
    if (!values.city) fields.push('City')
    if (!values.state) fields.push('State')
    if (!values.salaryType) fields.push('Salary Type')
    if (!values.salaryMin) fields.push('Min Salary')
    if (!values.salaryMax) fields.push('Max Salary')
    return fields
  }, [values])

  const handleSubmit = useCallback(async () => {
    if (missingRequiredFields.length > 0) {
      setShowError(true)
      return
    }

    setIsSubmitting(true)

    const jobData = {
      ...values,
      areaType: values.area,
      shiftType: values.candidateTypeId === NURSE_CANDIDATE_TYPE_ID ? values.shiftType : null,
    }

    try {
      const response = await fetchDispatch({
        path: '/api/kiosk/job',
        method: 'POST',
        values: {
          employerId: wizardData.employerId,
          employerName: wizardData.employerName,
          employerUserId: wizardData.employerUserId,
          firstName: wizardData.firstName,
          lastName: wizardData.lastName,
          email: wizardData.email,
          job: jobData,
        },
      })
      setWizardData(prev => ({ ...prev, matchCounts: response.matchCounts }))
      onNext(response)
    } catch (error) {
      dispatch(showSnackbar('Failed to post job. Please try again.', snackbarTypes.ERROR))
    } finally {
      setIsSubmitting(false)
    }
  }, [values, missingRequiredFields, wizardData, setWizardData, onNext, dispatch])

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Job Details
      </Typography>

      {showError && missingRequiredFields.length > 0 && (
        <Alert severity='error' sx={{ mb: 3 }}>
          Please fill in the following required fields: {missingRequiredFields.join(', ')}
        </Alert>
      )}

      <GenericCollapsableSection title='Basics' isOpenByDefault={true}>
        <BasicsSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
          candidateTypeOptions={candidateTypeOptions}
          formSpecialties={formSpecialties}
          formSubspecialties={formSubspecialties}
          setJobAlertData={() => {}}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Location' isOpenByDefault={true}>
        <LocationSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
          setJobAlertData={() => {}}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Compensation & Benefits' isOpenByDefault={true}>
        <CompensationSection
          values={values}
          setState={handleFieldChange}
          showError={showError}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Visa & Sponsorship' isOpenByDefault={false}>
        <VisaSection
          values={values}
          setState={handleFieldChange}
        />
      </GenericCollapsableSection>

      <GenericCollapsableSection title='Description' isOpenByDefault={false}>
        <DescriptionSection
          values={values}
          onEditorChange={handleEditorChange}
        />
      </GenericCollapsableSection>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : null}
        >
          {isSubmitting ? 'Posting...' : 'Post Job'}
        </Button>
      </Box>
    </Box>
  )
}

export default KioskJobFormStep
